import React, { useCallback, useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../../redux/store';
import axios from '../../interceptors/axios';

interface DsScreen {
  id: number;
  device_id: string;
  display_name: string | null;
  last_seen_at: string | null;
  app_version: string | null;
  ip_address: string | null;
  default_content_type: string | null;
  default_playlist_id: number | null;
  schedule_id: number | null;
  storage_free_bytes: number | null;
  storage_total_bytes: number | null;
  created_at: string;
}

interface DsSchedule {
  id: number;
  name: string;
  companyGroup: string;
  updatedAt: string;
}

const ONLINE_MS = 2 * 60 * 1000;
const isOnline = (last: string | null) =>
  !!last && Date.now() - new Date(last).getTime() < ONLINE_MS;

const glassSx = {
  background: 'var(--glass-bg)',
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  border: '1px solid var(--glass-border)',
};

// ── Icons ─────────────────────────────────────────────────────────────────────

const IcoChevron: React.FC = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

const IcoCalendar: React.FC = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

// ── KPI Card ─────────────────────────────────────────────────────────────────

interface KpiProps {
  label: string;
  value: number | string;
  accent: string;
  accentBg: string;
  onClick?: () => void;
}

const KpiCard: React.FC<KpiProps> = ({ label, value, accent, accentBg, onClick }) => (
  <Box onClick={onClick} sx={{
    borderRadius: '16px', p: '24px 26px',
    ...glassSx,
    display: 'flex', flexDirection: 'column', gap: '6px',
    position: 'relative', overflow: 'hidden',
    cursor: onClick ? 'pointer' : 'default',
    transition: 'transform .12s',
    '&:hover': onClick ? { transform: 'translateY(-1px)', boxShadow: '0 4px 16px rgba(0,0,0,.2)' } : {},
    '&::after': {
      content: '""', position: 'absolute', top: -20, right: -20,
      width: 100, height: 100, borderRadius: '50%',
      background: accentBg, filter: 'blur(24px)', pointerEvents: 'none',
    },
  }}>
    <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--text-disabled)' }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: 42, fontWeight: 700, color: accent, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>
      {value}
    </Typography>
  </Box>
);

// ── Main ──────────────────────────────────────────────────────────────────────

/** Показатели воспроизведения с дашборда отчётов — /api/reports/dashboard */
interface PlaybackDash {
  summary: { today: number; week: number; total: number; files_today: number };
  by_player: { device_id: string; display_name: string | null; plays: number; last_tally_at: string | null; last_seq: number }[];
  top_files: { file_id: number; name: string; plays: number }[];
  attention: {
    silent: { device_id: string; display_name: string | null; last_tally_at: string | null }[];
    gaps: { device_id: string; message: string; created_at: string }[];
  };
}

const DashboardPage: React.FC = () => {
  const { t } = useTranslation();
  const auth = useSelector((s: RootState) => s.auth.value);
  const navigate = useNavigate();

  const [screens, setScreens]     = useState<DsScreen[]>([]);
  const [schedules, setSchedules] = useState<DsSchedule[]>([]);
  const [loading, setLoading]     = useState(true);
  const [playback, setPlayback]   = useState<PlaybackDash | null>(null);

  const fetchData = useCallback(async () => {
    try {
      const [scrRes, schRes, pbRes] = await Promise.all([
        axios.get<DsScreen[]>('/screens/devices'),
        axios.get<DsSchedule[]>('/schedules'),
        axios.get<PlaybackDash>('/reports/dashboard'),
      ]);
      setScreens(scrRes.data);
      setSchedules(schRes.data);
      setPlayback(pbRes.data);
    } catch { /* interceptor */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (auth) fetchData();
    // auto-refresh every 30s
    const id = setInterval(() => { if (auth) fetchData(); }, 30_000);
    return () => clearInterval(id);
  }, [auth, fetchData]);

  const online  = screens.filter(s => isOnline(s.last_seen_at)).length;
  const offline = screens.filter(s => !isOnline(s.last_seen_at)).length;

  const recentScreens = [...screens]
    .sort((a, b) => {
      if (!a.last_seen_at && !b.last_seen_at) return 0;
      if (!a.last_seen_at) return 1;
      if (!b.last_seen_at) return -1;
      return new Date(b.last_seen_at).getTime() - new Date(a.last_seen_at).getTime();
    })
    .slice(0, 8);

  const formatSeen = (last: string | null) => {
    if (!last) return 'never';
    const s = Math.round((Date.now() - new Date(last).getTime()) / 1000);
    if (s < 60)   return `${s}s ago`;
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
  };

  const formatScheduleDate = (d: string) => {
    const diff = Math.floor((Date.now() - new Date(d).getTime()) / 1000);
    if (diff < 60)   return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return new Date(d).toLocaleDateString();
  };

  if (loading) return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', gap: '10px' }}>
      <Box sx={{
        width: 20, height: 20, borderRadius: '50%',
        border: '2px solid var(--glass-border)', borderTopColor: 'var(--accent)',
        animation: 'spin .7s linear infinite',
        '@keyframes spin': { to: { transform: 'rotate(360deg)' } },
      }} />
      <Typography sx={{ fontSize: 13, color: 'var(--text-disabled)' }}>Loading…</Typography>
    </Box>
  );

  return (
    <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: '16px', display: 'flex', flexDirection: 'column', gap: '12px', minWidth: 0 }}>

      {/* ── KPI row ── */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '12px' }}>
        <KpiCard label={t('devices.total')}   value={screens.length} accent="var(--accent)" accentBg="rgba(99,102,241,.15)" onClick={() => navigate('/screens')} />
        <KpiCard label={t('devices.online')}  value={online}         accent="#34d399"        accentBg="rgba(52,211,153,.12)"  onClick={() => navigate('/screens?filter=online')} />
        <KpiCard label={t('devices.offline')} value={offline}        accent="#f87171"        accentBg="rgba(248,113,113,.12)" onClick={() => navigate('/screens?filter=offline')} />
      </Box>

      {/* ── Требует внимания: тихие приставки и потерянные пакеты счёта ── */}
      {playback && (playback.attention.silent.length > 0 || playback.attention.gaps.length > 0) && (
        <Box sx={{ borderRadius: '14px', overflow: 'hidden', ...glassSx }}>
          <Box sx={{ px: '18px', py: '9px', borderBottom: '1px solid var(--divider)', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#fbbf24', flexShrink: 0 }} />
            <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{t('dashboard.attention')}</Typography>
          </Box>
          {playback.attention.silent.map(s2 => (
            <Box key={s2.device_id} sx={{ display: 'flex', alignItems: 'center', gap: '12px', px: '18px', py: '9px', borderBottom: '1px solid var(--divider)' }}>
              <Box sx={{ px: '8px', py: '2px', borderRadius: '20px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
                bgcolor: 'rgba(248,113,113,.12)', color: '#f87171', flexShrink: 0 }}>{t('dashboard.noTally')}</Box>
              <Typography sx={{ fontSize: 13, minWidth: 160 }}>{s2.display_name || s2.device_id}</Typography>
              <Typography sx={{ fontSize: 12, color: 'var(--text-disabled)', flex: 1 }}>{t('dashboard.noTallyHint')}</Typography>
            </Box>
          ))}
          {playback.attention.gaps.slice(0, 3).map((g, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: '12px', px: '18px', py: '9px', borderBottom: '1px solid var(--divider)' }}>
              <Box sx={{ px: '8px', py: '2px', borderRadius: '20px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
                bgcolor: 'rgba(251,191,36,.12)', color: '#fbbf24', flexShrink: 0 }}>{t('dashboard.gap')}</Box>
              <Typography sx={{ fontSize: 13, minWidth: 160, fontFamily: '"JetBrains Mono", monospace' }}>{g.device_id}</Typography>
              <Typography sx={{ fontSize: 12, color: 'var(--text-disabled)', flex: 1 }}>{g.message}</Typography>
            </Box>
          ))}
        </Box>
      )}


      {/* ── Main 2-col grid ── */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: '16px', alignItems: 'start' }}>

        {/* ── Screen activity feed ── */}
        <Box sx={{ borderRadius: '14px', overflow: 'hidden', ...glassSx }}>
          <Box sx={{ px: '18px', py: '9px', borderBottom: '1px solid var(--divider)', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Typography sx={{ flex: 1, fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
              {t('dashboard.activity')}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Box sx={{
                width: 6, height: 6, borderRadius: '50%', bgcolor: '#34d399',
                boxShadow: '0 0 5px rgba(52,211,153,.7)',
                animation: 'pulse 2s ease infinite',
                '@keyframes pulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: .4 } },
              }} />
              <Typography sx={{ fontSize: 11, color: '#34d399', fontWeight: 600 }}>
                {t('dashboard.live')}
              </Typography>
            </Box>
          </Box>

          {recentScreens.length === 0 ? (
            <Box sx={{ py: '32px', textAlign: 'center' }}>
              <Typography sx={{ fontSize: 13, color: 'var(--text-disabled)' }}>No screens yet</Typography>
            </Box>
          ) : recentScreens.map(scr => {
            const online = isOnline(scr.last_seen_at);
            const dotColor = online ? '#34d399' : '#f87171';
            const dotGlow  = online ? 'rgba(52,211,153,.5)' : 'rgba(248,113,113,.5)';
            const rowBg    = online ? 'transparent' : 'rgba(248,113,113,.04)';

            return (
              <Box
                key={scr.id}
                onClick={() => navigate(`/screens/${scr.id}`)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: '12px',
                  px: '16px', py: '10px',
                  borderBottom: '1px solid var(--divider)',
                  bgcolor: rowBg,
                  cursor: 'pointer',
                  '&:last-child': { borderBottom: 'none' },
                  '&:hover': { bgcolor: 'var(--row-hover)' },
                  transition: 'background-color .12s',
                }}
              >
                <Box sx={{
                  width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                  bgcolor: dotColor, boxShadow: `0 0 6px ${dotGlow}`,
                }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)', fontFamily: '"JetBrains Mono", monospace', letterSpacing: '.04em', flexShrink: 0 }}>
                      {scr.device_id}
                    </Typography>
                    {scr.display_name && (
                      <Typography sx={{ fontSize: 12, color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {scr.display_name}
                      </Typography>
                    )}
                  </Box>
                  {scr.ip_address && (
                    <Typography sx={{ fontSize: 11, color: 'var(--text-disabled)', fontFamily: '"JetBrains Mono", monospace' }}>
                      {scr.ip_address}
                    </Typography>
                  )}
                </Box>
                <Typography sx={{ fontSize: 11, color: 'var(--text-disabled)', flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
                  {formatSeen(scr.last_seen_at)}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {/* ── Schedules list ── */}
        <Box sx={{ borderRadius: '14px', overflow: 'hidden', ...glassSx, display: { xs: 'none', md: 'block' } }}>
          <Box sx={{ px: '18px', py: '9px', borderBottom: '1px solid var(--divider)', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Typography sx={{ flex: 1, fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
              {t('nav.schedule')}
            </Typography>
            <Box
              component="button"
              onClick={() => navigate('/schedules')}
              sx={{
                display: 'inline-flex', alignItems: 'center', gap: '3px',
                px: '8px', py: '4px', borderRadius: '7px', cursor: 'pointer',
                color: 'var(--accent)', fontSize: 11, fontWeight: 600,
                border: '1px solid transparent', background: 'none',
                '&:hover': { bgcolor: 'var(--glass-hover)', borderColor: 'var(--glass-border)' },
                transition: 'all .12s',
              }}
            >
              {t('dashboard.details')} <IcoChevron />
            </Box>
          </Box>

          {schedules.length === 0 ? (
            <Box sx={{ py: '32px', textAlign: 'center' }}>
              <Typography sx={{ fontSize: 13, color: 'var(--text-disabled)' }}>{t('dashboard.emptySchedule')}</Typography>
            </Box>
          ) : schedules.slice(0, 8).map(sch => (
            <Box
              key={sch.id}
              onClick={() => navigate(`/schedules/${sch.id}`)}
              sx={{
                display: 'flex', alignItems: 'center', gap: '12px',
                px: '16px', py: '10px',
                borderBottom: '1px solid var(--divider)',
                cursor: 'pointer',
                '&:last-child': { borderBottom: 'none' },
                '&:hover': { bgcolor: 'var(--row-hover)' },
                transition: 'background-color .12s',
              }}
            >
              <Box sx={{
                width: 28, height: 28, borderRadius: '7px', flexShrink: 0,
                bgcolor: 'rgba(167,139,250,.12)', border: '1px solid rgba(167,139,250,.2)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#a78bfa',
              }}>
                <IcoCalendar />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {sch.name}
                </Typography>
                {sch.companyGroup && (
                  <Typography sx={{ fontSize: 11, color: 'var(--text-disabled)' }}>
                    {sch.companyGroup}
                  </Typography>
                )}
              </Box>
              <Typography sx={{ fontSize: 11, color: 'var(--text-disabled)', flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
                {formatScheduleDate(sch.updatedAt)}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

    </Box>
  );
};

export default DashboardPage;
